import { observer } from 'mobx-react-lite'
import { useRef } from 'react'
import { upperFirst } from 'lodash'
import { Button, Divider, Label, Typography, Warning, Error } from 'shared/ui'
import {
  makeTextFieldBlurOnEnter,
  makeTextFieldHighlightLimit,
  TextField,
} from 'shared/ui/TextField'
import { USER_FIRST_NAME_LIMIT, USER_LAST_NAME_LIMIT } from 'shared/constants/limits'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { PhoneNumberInput } from 'shared/ui/PhoneNumberInput'
import { useAccountContext } from 'pages/settings/pages/personal/pages/account/context/accountContext'
import styles from './styles.module.scss'

export const ProfilePage = observer(() => {
  const { profileStore } = useAccountContext()
  const {
    firstName,
    setFirstName,
    limitFirstNameReached,
    setLimitFirstNameReached,
    lastName,
    setLastName,
    limitLastNameReached,
    setLimitLastNameReached,
    email,
    setEmail,
    setNumber,
    timezone,
    phoneNumberInputStore,
    timezoneItems,
    loading,
    errorsMap,
    onSaveProfile,
    handleTimezone,
    openChangePasswordModal,
    handleLogoutAllDevices,
    isFieldChanged,
  } = profileStore
  const emailRef = useRef<HTMLInputElement | null>(null)

  const handleBlur = () => {
    if (limitFirstNameReached || limitLastNameReached) return

    void onSaveProfile()
  }

  const handlerEmailFocus = () => {
    emailRef?.current?.blur()
    setEmail()
  }

  return (
    <div>
      <div className={styles.formWrap}>
        <div className={styles.inputWrap}>
          <Label
            tertiary={true}
            label={{
              text: 'First name',
            }}
          />
          <TextField
            onBlur={() => {
              if (isFieldChanged('first_name', firstName)) setFirstName(upperFirst(firstName))
              handleBlur()
            }}
            InputProps={{
              placeholder: 'First name',
            }}
            disabled={loading}
            size='medium'
            variant='stroke'
            value={firstName}
            onChange={setFirstName}
            error={errorsMap.get('first_name')}
            mainActions={[
              makeTextFieldHighlightLimit({
                limit: USER_FIRST_NAME_LIMIT,
                onChange: setLimitFirstNameReached,
              }),
              makeTextFieldBlurOnEnter(),
            ]}
          />
          {limitFirstNameReached && (
            <Warning text={`${USER_FIRST_NAME_LIMIT}-character limit reached`} />
          )}
          {errorsMap.get('first_name') && (
            <Error type={'text'} error={errorsMap.get('first_name')} />
          )}
        </div>
        <div className={styles.inputWrap}>
          <Label
            tertiary={true}
            label={{
              text: 'Last name',
            }}
          />
          <TextField
            onBlur={() => {
              if (isFieldChanged('last_name', lastName)) setLastName(upperFirst(lastName))
              handleBlur()
            }}
            InputProps={{
              placeholder: 'Last name',
            }}
            variant='stroke'
            size='medium'
            disabled={loading}
            value={lastName}
            onChange={setLastName}
            error={errorsMap.get('last_name')}
            mainActions={[
              makeTextFieldHighlightLimit({
                limit: USER_LAST_NAME_LIMIT,
                onChange: setLimitLastNameReached,
              }),
              makeTextFieldBlurOnEnter(),
            ]}
          />
          {limitLastNameReached && (
            <Warning text={`${USER_LAST_NAME_LIMIT}-character limit reached`} />
          )}
          {errorsMap.get('last_name') && <Error type={'text'} error={errorsMap.get('last_name')} />}
        </div>
        <div className={styles.inputWrap}>
          <Label
            tertiary={true}
            label={{
              text: 'Email',
            }}
          />
          <TextField
            InputProps={{
              placeholder: 'Email',
              ref: emailRef,
            }}
            variant='stroke'
            size='medium'
            value={email}
            onFocus={handlerEmailFocus}
            disabled={loading}
            className={styles.emailInput}
          />
        </div>
        <div className={styles.inputWrap}>
          <Label
            tertiary={true}
            label={{
              text: 'Phone number',
            }}
          />
          <div className={styles.phoneWrap} onClick={setNumber}>
            <PhoneNumberInput
              item={phoneNumberInputStore.country}
              items={phoneNumberInputStore.countries}
              number={phoneNumberInputStore.number}
              setNumber={phoneNumberInputStore.setNumber}
              setCountry={phoneNumberInputStore.setCountry}
              numberError={phoneNumberInputStore.numberError}
              triggerNumberValidation={phoneNumberInputStore.triggerNumberValidation}
              numberRequestError={
                phoneNumberInputStore.numberRequestError || errorsMap.get('phone_initial_number')
              }
              hideLabel
              loading={loading}
              readOnly={true}
              disabled={loading}
            />
          </div>
        </div>
        <div className={styles.inputWrap}>
          <SelectField
            boxWidth={'100%'}
            labelText={'Timezone'}
            value={timezone}
            size='medium'
            setValue={handleTimezone}
            disabled={loading}
            dropdownProps={{
              propsDropdownList: {
                withGrouping: true,
                dropdownGroupProps: {
                  isScroll: false,
                },
              },
            }}
            items={timezoneItems}
          />
        </div>
      </div>
      <Divider sx={{ margin: '0px 4px', borderColor: 'var(--border-neutral-default)' }} />
      <div className={styles.actionsWrap}>
        <div className={styles.action}>
          <div className={styles.label}>
            <Typography
              variant='body-md-medium'
              color='var(--content-primary-primary)'
              ariaLabel='Reset password title'
            >
              Password
            </Typography>
            <Typography
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel='Reset password description'
            >
              Change your password to keep your account secure
            </Typography>
          </div>
          <Button
            disabled={loading}
            contained='secondary'
            size='medium'
            onClick={openChangePasswordModal}
            icon='refresh'
            text='Change password'
          />
        </div>
        <div className={styles.action}>
          <div className={styles.label}>
            <Typography
              variant='body-md-medium'
              color='var(--content-primary-primary)'
              ariaLabel='Log out title'
            >
              Log out
            </Typography>
            <Typography
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel='Log out description'
            >
              Log out of all active sessions on all devices
            </Typography>
          </div>
          <Button
            contained='secondary'
            size='medium'
            onClick={handleLogoutAllDevices}
            icon='logOut'
            text='Log out all devices'
          />
        </div>
      </div>
    </div>
  )
})
